import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  InputDateField,
  InputTextField,
  KeyValuePairs,
} from '@/components';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getPaymentsInquiry } from '@/services/getPaymentsInquiry';
import { getPaymentsInquiryExport } from '@/services/getPaymentsInquiryExport';
import { useMutation } from '@tanstack/react-query';

import TabHeader from '@/components/TabHeader/TabHeader';
import CheckBox from '@/components/CheckBox/CheckBox';
import { useToastNotification } from '@/hooks';
import {
  faReply,
  faShare,
  faBox,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Divider } from 'primereact/divider';
import { TabPanel, TabView } from 'primereact/tabview';
import { useTranslation } from 'react-i18next';
import styles from './PaymentInquiry.module.scss';
import InvoiceItemList, { ItemProps } from './InvoiceItem';
import { necesitaPreguntarPorEarlyArrival } from '../ImportFCL/services/necesitaPreguntarPorEarlyArrival';
import { getOidVisita } from '../ImportFCL/services/getOidVisita';
import { parsePrecintoData } from '../ImportFCL/helpers';

type PaymentInquiryProps = {
  codViaje?: number;
  buqueName?: string;
  armador?: string;
};

enum Tabs {
  Import = 0,
  Export = 1,
}

type QueryType = {
  marca: string;
  numero: string;
  fecha: string;
  servicio: string;
  entregaDirecta: boolean;
  ingresoTardio: boolean;
};

const PaymentInquiry: FC<PaymentInquiryProps> = () => {
  const { showToast } = useToastNotification();
  const { t } = useTranslation('paymentInquiry');

  const [importData, setImportData] = useState<unknown>();
  const [importTotal, setImportTotal] = useState(0);
  const [importResultado, setImportResultado] = useState('');
  const [exportData, setExportData] = useState<unknown>();
  const [exportTotal, setExportTotal] = useState(0);
  const [value, setValue] = useState('');
  const [date, setDate] = useState<Date | null>(new Date());
  const [entregaDirecta, setEntregaDirecta] = useState(false);
  const [isLateArrival, setIsLateArrival] = useState(false);
  const [lateArrival, setLateArrival] = useState(false);
  const [query, setQuery] = useState<QueryType>({
    marca: '',
    numero: '',
    fecha: '',
    servicio: '',
    entregaDirecta: false,
    ingresoTardio: false,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  const regexValidate = /^[A-Za-z]{4}\d{7}$/;

  const {
    mutate: getImportData,
    isSuccess: importIsSuccess,
    error: importError,
    isPending: importIsPending,
  } = useMutation({
    mutationFn: getPaymentsInquiry,
    onSuccess: ({ items, total, resultado }) => {
      setImportData(items);
      setImportTotal(total);
      setImportResultado(resultado);
    },
    onError: (error) => {
      if (error?.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      }
    },
  });

  const {
    mutate: getExportData,
    isSuccess: exportIsSuccess,
    error: exportError,
    isPending: exportIsPending,
  } = useMutation({
    mutationFn: getPaymentsInquiryExport,
    onSuccess: ({ items, total }) => {
      setExportData(items);
      setExportTotal(total);
    },
    onError: () =>
      showToast({ severity: 'error', detail: t('unexpectedError') }),
  });

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value: inputValue } = e.target;
    inputValue = inputValue?.replace(/[^A-Za-z0-9]/g, '');

    if (inputValue?.length > 11) return;

    const marca = inputValue.slice(0, 4);
    const numero = inputValue.slice(4, 11);

    if (marca && !marca.match(/^[A-Za-z]+$/)) return;
    if (numero && !numero.match(/^[0-9]+$/)) return;

    setValue(inputValue?.toUpperCase());
  };
  const checkIsValid = useCallback(() => {
    const regex = /^[^\d]*$/;
    if (typeof value !== 'string') return false;
    const marca = value.slice(0, 4);
    const numero = value.slice(4);
    if (numero.length < 7) return false;
    if (Number.isNaN(Number(numero))) return false;

    if (!regex.test(marca)) return false;
    return true;
  }, [value]);

  const onSearch = (): void | null => {
    if (!regexValidate.test(value)) {
      showToast({ severity: 'error', detail: t('error') });
      return;
    }
    const isValid = checkIsValid();
    if (!isValid) return;

    const fecha = date?.toISOString() ?? '';
    const marca = value.slice(0, 4);
    const numero = value.slice(4);

    const body: QueryType = {
      marca,
      numero,
      fecha,
      servicio: '',
      entregaDirecta,
      ingresoTardio: lateArrival,
    };

    if (activeIndex === Tabs.Import) {
      getImportData(body);
    } else {
      getExportData(body);
    }

    setQuery(body);
  };

  const handleDateChange = (newDate: Date | null | undefined) => {
    setDate(newDate ?? null);

    const fecha = date?.toISOString() ?? '';
    setQuery((prevState) => ({
      ...prevState,
      fecha,
    }));
  };
  const isLoading = importIsPending || exportIsPending;

  useEffect(() => {
    setValue('');
    setDate(new Date());
    setEntregaDirecta(false);
    setQuery({
      marca: '',
      numero: '',
      fecha: '',
      servicio: '',
      entregaDirecta: false,
      ingresoTardio: false,
    });
    setImportData(undefined);
    setImportTotal(0);
    setImportResultado('');
    setExportData(undefined);
    setExportTotal(0);
  }, [activeIndex]);

  const validateLateArrival = useCallback(
    async (data: { oidVisita: number }) => {
      if (!date) return;
      const body = {
        oidVisita: data?.oidVisita,
        fecha: date?.toISOString() || '',
      };
      const isLate = await necesitaPreguntarPorEarlyArrival(body);
      setIsLateArrival(isLate);
    },
    [date]
  );

  const { mutate: getContainerData } = useMutation({
    mutationFn: getOidVisita,
    onSuccess: (data) => validateLateArrival(data),
  });

  useEffect(() => {
    const isValid = checkIsValid();
    if (!isValid || !date) return;
    const marca = value.slice(0, 4);
    const numero = value.slice(4);

    const body = { marca, numero };
    getContainerData(body);
  }, [getContainerData, value, checkIsValid, date]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getErrorMessage = (error: any) => {
    if (typeof error === 'object' || error !== null) {
      return '';
    }
    return error;
  };

  const precintoData = parsePrecintoData(importResultado);

  const precintoDataValuePairs = (data: unknown[]) => {
    return data.map((item) => {
      const typedItem = item as {
        label: string;
        value: string | null;
        items: { label: string }[];
      };
      return {
        key: typedItem.label,
        value:
          typedItem.items && typedItem.items.length > 0
            ? typedItem.items[0].label
            : typedItem.value,
      };
    });
  };

  return (
    <Container title={t('title')} className={styles.containerTap}>
      <div className={styles.containerTabs}>
        <div className={styles.tabsAndIdentifier}>
          <TabView
            className={styles.tabView}
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <TabPanel
              header={
                <TabHeader
                  active={activeIndex === Tabs.Import}
                  label={t('import')}
                  icon={faReply}
                />
              }
            >
              {activeIndex === Tabs.Import && (
                <div className={styles.container}>
                  <div className={styles.containerLeft}>
                    <InputTextField
                      id="container"
                      name=""
                      value={value}
                      onChange={onInputChange}
                      label={t('container')}
                      placeholder={t('placeholderContainer')}
                      iconLabel={faBox}
                    />
                    <InputDateField
                      id="startAt"
                      name="startAt"
                      value={date}
                      className={styles.dateField}
                      onChange={handleDateChange}
                      label={t('withdrawalImport')}
                      readOnlyInput
                      iconLabel={faCalendarAlt}
                    />
                    <CheckBox
                      label={t('directDelivery')}
                      checked={entregaDirecta}
                      onChange={(isChecked) => setEntregaDirecta(isChecked)}
                    />
                    <div className={styles.buttonContainer}>
                      <Button
                        className={styles.button}
                        onClick={onSearch}
                        disabled={!regexValidate.test(value)}
                      >
                        <span>{t('consult')}</span>
                      </Button>
                    </div>
                  </div>
                  <Divider layout="vertical" className={styles.dividerMobil} />
                  <div className={styles.containerResult}>
                    <div className={styles.containerName}>
                      <div>
                        <h4>{t('calculationResults')}</h4>
                      </div>
                      <div>
                        {importIsSuccess && (
                          <h4 className={styles.titleContainer}>
                            {t('container')}
                            <span className={styles.containerData}>
                              {` ${query?.marca} ${query?.numero}`}
                            </span>
                          </h4>
                        )}
                      </div>
                    </div>

                    {activeIndex === Tabs.Import &&
                      (() => {
                        const hasValidImportData =
                          importIsSuccess &&
                          importData &&
                          Array.isArray(importData) &&
                          importData.length > 0;

                        if (hasValidImportData) {
                          return (
                            <div className="mt-4">
                              <KeyValuePairs
                                pairs={precintoDataValuePairs(precintoData)}
                                color="grey"
                              />
                            </div>
                          );
                        }

                        return null;
                      })()}

                    <div className={styles.containerItemList}>
                      {(() => {
                        const hasValidImportData =
                          importIsSuccess &&
                          importData &&
                          Array.isArray(importData) &&
                          importData.length > 0;

                        if (hasValidImportData) {
                          return (
                            <InvoiceItemList
                              items={importData as ItemProps[]}
                              total={importTotal}
                            />
                          );
                        }

                        if (!isLoading && !importError) {
                          return <p>{t('text')}</p>;
                        }

                        return null;
                      })()}
                      {importIsPending && (
                        <div className="flex justify-content-center align-items-center h-full w-full">
                          <ProgressSpinner aria-label="Loading" />
                        </div>
                      )}
                      {!!importError && (
                        <p>{getErrorMessage(importError?.message)}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabPanel>

            <TabPanel
              header={
                <TabHeader
                  active={activeIndex === Tabs.Export}
                  label={t('export')}
                  icon={faShare}
                />
              }
            >
              {activeIndex === Tabs.Export && (
                <div className={styles.container}>
                  <div className={styles.containerLeft}>
                    <InputTextField
                      id="container"
                      name=""
                      value={value}
                      onChange={onInputChange}
                      label={t('container')}
                      placeholder={t('placeholderContainer')}
                      iconLabel={faBox}
                    />
                    <InputDateField
                      id="startAt"
                      name="startAt"
                      value={date}
                      className={styles.dateField}
                      onChange={handleDateChange}
                      label={t('withdrawal')}
                      readOnlyInput
                      iconLabel={faCalendarAlt}
                    />

                    {isLateArrival && (
                      <CheckBox
                        label={t('lateEntry')}
                        style={{ width: '100%' }}
                        className="mt-3 md:mt-0"
                        checked={lateArrival}
                        onChange={(e) => setLateArrival(e)}
                      />
                    )}

                    <div className={styles.buttonContainer}>
                      <Button
                        className={styles.button}
                        onClick={onSearch}
                        disabled={!regexValidate.test(value)}
                      >
                        <span>{t('consult')}</span>
                      </Button>
                    </div>
                  </div>
                  <Divider layout="vertical" className={styles.dividerMobil} />
                  <div className={styles.containerResult}>
                    <div className={styles.containerName}>
                      <div>
                        <h4>{t('calculationResults')}</h4>
                      </div>
                      <div>
                        {!!exportIsSuccess && (
                          <h4 className={styles.titleContainer}>
                            {t('container')}{' '}
                            <span className={styles.containerData}>
                              {' '}
                              {`${query?.marca} ${query?.numero}`}
                            </span>
                          </h4>
                        )}
                      </div>
                    </div>

                    <div className={styles.containerItemList}>
                      {(() => {
                        const hasValidExportData =
                          exportIsSuccess &&
                          exportData &&
                          Array.isArray(exportData) &&
                          exportData.length > 0;

                        if (hasValidExportData) {
                          return (
                            <InvoiceItemList
                              items={
                                (exportData as ItemProps[])?.slice(1) ?? []
                              }
                              total={exportTotal}
                            />
                          );
                        }

                        if (!isLoading && !exportError) {
                          return <p>{t('text')}</p>;
                        }

                        return null;
                      })()}
                      {exportIsPending && (
                        <div className="flex justify-content-center align-items-center h-full w-full">
                          <ProgressSpinner aria-label="Loading" />
                        </div>
                      )}
                      {!!exportError && (
                        <div>
                          <p>CONSULTA DE EXPORTACION.</p>
                          <p className={styles.errorDescription}>
                            {`${query?.marca} ${query?.numero}`}
                          </p>
                          <p className={styles.error}>{exportError?.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabPanel>
          </TabView>
        </div>
      </div>
    </Container>
  );
};

export default PaymentInquiry;
